import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react';
import {
  useDataChannel,
  useChat,
  useRemoteParticipants,
  useLocalParticipant,
  useTracks,
} from '@livekit/components-react';
import {RoomEvent, Track} from 'livekit-client';
import {TranscriptionTile} from './TranscriptionTile';
import {useSelector} from 'react-redux';
import LiveKitTextInput from './LiveKitTextInput';
import LiveKitTranscriptPanel from './LiveKitTranscriptPanel';
import {
  latestAgentChatMessage,
  TUTOR_CLIENT_READY_MESSAGE,
  TUTOR_OPENING_RECEIVED_PREFIX,
} from './tutorChatMessages';

const MIC_PERMISSION_DENIED_PATTERNS = [
  'notallowederror',
  'permissiondenied',
  'denied permission',
  'permission denied',
  'microphone permission',
  'securityerror',
];

const isMicrophonePermissionDeniedError = error => {
  const details = [
    error?.name,
    error?.message,
    error?.cause?.message,
    String(error || ''),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return MIC_PERMISSION_DENIED_PATTERNS.some(pattern => details.includes(pattern));
};

export default function LiveKitSDK({micPermissionDenied = false}) {
  const tracks = useTracks();
  const participants = useRemoteParticipants({
    updateOnlyOn: [RoomEvent.ParticipantMetadataChanged],
  });
  const agentParticipant = participants.find(p => p.isAgent);
  const {
    localParticipant,
    isMicrophoneEnabled,
    microphoneTrack,
    lastMicrophoneError,
  } = useLocalParticipant();
  const {chatMessages, send: sendChatMessage} = useChat();

  const oneToOneMeetingStatus = useSelector(
    state => state.communication.speechToTextService.oneToOneMeetingStatus,
  );
  const tutorMode = useSelector(
    state => state.communication.speechToTextService.tutorMode || 'VOICE',
  );

  // Guardrail: ensure mic is enabled in 1:1 tutor mode.
  // If the mic gets disabled (permissions reset, device switch, etc) the agent won't hear the user,
  // which matches the "tutor asks first question then never responds" symptom.
  useEffect(() => {
    if (!oneToOneMeetingStatus || tutorMode !== 'VOICE') return;
    if (!localParticipant) return;
    if (isMicrophoneEnabled) return;
    Promise.resolve(localParticipant.setMicrophoneEnabled(true)).catch(e => {
      console.warn('[LiveKit] Failed to enable microphone', e);
    });
  }, [oneToOneMeetingStatus, tutorMode, localParticipant, isMicrophoneEnabled]);

  useEffect(() => {
    if (!oneToOneMeetingStatus || tutorMode !== 'VOICE') return;
    if (lastMicrophoneError) {
      console.warn('[LiveKit] lastMicrophoneError', lastMicrophoneError);
    }
  }, [oneToOneMeetingStatus, tutorMode, lastMicrophoneError]);

  const micPermissionDeniedFromLiveKit = useMemo(
    () => isMicrophonePermissionDeniedError(lastMicrophoneError),
    [lastMicrophoneError],
  );
  const showMicPermissionWarning =
    oneToOneMeetingStatus &&
    tutorMode === 'VOICE' &&
    !isMicrophoneEnabled &&
    (micPermissionDenied || micPermissionDeniedFromLiveKit);

  useEffect(() => {
    if (!oneToOneMeetingStatus) return;
    if (!microphoneTrack) {
      console.warn('[LiveKit] microphoneTrack publication is missing (not published yet?)');
      return;
    }
    console.log('[LiveKit] microphoneTrack', {
      sid: microphoneTrack.trackSid,
      muted: microphoneTrack.isMuted,
      kind: microphoneTrack.kind,
      source: microphoneTrack.source,
      hasTrack: Boolean(microphoneTrack.track),
    });
  }, [oneToOneMeetingStatus, microphoneTrack]);

  // How long the tutor-transcript stream must stay quiet before we hand the turn
  // back to you ('prompt'). We drive 'speaking' from the tutor's transcript
  // activity, NOT LiveKit VAD: on web the agent's VAD (isSpeaking) misses long
  // stretches of tutor audio — observed ~18s of reported "silence" mid-answer —
  // which flipped the UI to 'prompt' while the tutor was still talking. The
  // transcript stream (livekit_final_response … tutor) is continuous and
  // reliable, so this debounce only has to bridge short inter-sentence gaps.
  const AGENT_RELEASE_MS = 1500;

  // Single source of truth for the turn state:
  //   'connecting' – tutor hasn't joined the room yet
  //   'speaking'   – tutor is talking (you can't type/speak)
  //   'thinking'   – you submitted a question (typed send OR a final spoken
  //                  transcript); the tutor is processing (you can't type/speak)
  //   'prompt'     – your turn: type or speak
  // 'thinking' is entered ONLY on a real submission (see onTranscript and
  // onMessageSent), never from raw VAD — so noise can't flip it. Connection and
  // the tutor's own speech drive the rest.
  const [turn, setTurn] = useState('connecting');
  const releaseTimeout = useRef();

  // React-side transcript (Unity rendering disabled)
  const [transcriptSourceKey, setTranscriptSourceKey] = useState('');
  const [transcriptSourceText, setTranscriptSourceText] = useState('');
  const renderedTutorChatRef = useRef('');
  const clientReadySentRef = useRef(false);
  const openingAckSentRef = useRef(false);

  // Reset turn state whenever tutor mode (re)connects so a stale label doesn't
  // carry over.
  useEffect(() => {
    clearTimeout(releaseTimeout.current);
    renderedTutorChatRef.current = '';
    clientReadySentRef.current = false;
    openingAckSentRef.current = false;
    setTurn('connecting');
    setTranscriptSourceKey('');
    setTranscriptSourceText('');
  }, [oneToOneMeetingStatus]);

  // Text-mode tutor responses arrive through LiveKit chat rather than audio transcription.
  // Mirror the newest agent-authored message into the same transcript panel used by voice mode.
  useEffect(() => {
    if (!oneToOneMeetingStatus || !Array.isArray(chatMessages)) return;
    const latest = latestAgentChatMessage(chatMessages);
    if (!latest) return;
    const identity = latest.id;
    if (tutorMode === 'TEXT' && !openingAckSentRef.current) {
      openingAckSentRef.current = true;
      Promise.resolve()
        .then(() => sendChatMessage(`${TUTOR_OPENING_RECEIVED_PREFIX}:${identity}`))
        .catch(() => {
          openingAckSentRef.current = false;
        });
    }
    if (renderedTutorChatRef.current === identity) return;
    renderedTutorChatRef.current = identity;
    setTranscriptSourceKey(`tutor:chat_${identity}`);
    setTranscriptSourceText(latest.text);
    clearTimeout(releaseTimeout.current);
    setTurn('prompt');
  }, [chatMessages, oneToOneMeetingStatus, tutorMode, sendChatMessage]);

  // The tutor is "connected" once its participant has joined the room.
  const connected = !!agentParticipant;

  // Acknowledge that the browser has installed its chat observers before the worker publishes the
  // opening. The worker ignores this reserved protocol message and retains a legacy timeout for
  // older clients.
  useEffect(() => {
    if (
      !oneToOneMeetingStatus ||
      tutorMode !== 'TEXT' ||
      !connected ||
      clientReadySentRef.current
    ) return;
    let cancelled = false;
    let retryTimer;
    let attempts = 0;
    clientReadySentRef.current = true;
    const acknowledgeReady = () => {
      attempts += 1;
      Promise.resolve()
        .then(() => sendChatMessage(TUTOR_CLIENT_READY_MESSAGE))
        .catch(() => {
          if (cancelled) return;
          clientReadySentRef.current = false;
          if (attempts >= 3) return;
          retryTimer = setTimeout(() => {
            if (cancelled || clientReadySentRef.current) return;
            clientReadySentRef.current = true;
            acknowledgeReady();
          }, 300);
        });
    };
    acknowledgeReady();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [oneToOneMeetingStatus, tutorMode, connected, sendChatMessage]);

  // Connection drives connecting → prompt. When the tutor's participant is gone
  // (not connected) we show 'connecting'; once it joins we hand the floor to you
  // ('prompt') until the tutor actually starts speaking — the transcript handler
  // below flips it to 'speaking'. 'thinking' and 'speaking' are set from real
  // events (your submission / the tutor's transcript), never from VAD.
  useEffect(() => {
    if (!connected) {
      clearTimeout(releaseTimeout.current);
      setTurn('connecting');
      return;
    }
    setTurn(prev => (prev === 'connecting' ? 'prompt' : prev));
  }, [connected]);

  // — your transcription + data‐channel logic unchanged —
  const aat = tracks.find(
    t => t.publication.kind === Track.Kind.Audio && t.participant.isAgent,
  );
  let agentAudioTrack;
  if (aat) agentAudioTrack = aat;
  else if (agentParticipant) {
    agentAudioTrack = {
      participant: agentParticipant,
      source: Track.Source.Microphone,
    };
  }

  const chatTileContent = useMemo(() => {
    if (!agentAudioTrack) return null;
    return (
      <TranscriptionTile
        agentAudioTrack={agentAudioTrack}
        accentColor="#FF0000"
        onTranscript={({id, text, final, participant}) => {
          setTranscriptSourceKey(`${participant}:${id}`);
          setTranscriptSourceText(text || '');
          if (participant === 'player') {
            // Your spoken question: hand the turn to the tutor on a final,
            // non-empty line (the moment it's sent as livekit_final_response …
            // player). Interim/empty lines don't count, so noise can't flip it.
            if (final && (text || '').trim()) {
              setTurn('thinking');
            }
            return;
          }
          if (participant === 'tutor') {
            // Tutor transcript is streaming → the tutor is actively speaking.
            // This is the reliable "tutor speaking" signal on web. Re-arm the
            // debounce on every segment; when the stream goes quiet for
            // AGENT_RELEASE_MS, hand the turn back to you ('prompt').
            setTurn('speaking');
            clearTimeout(releaseTimeout.current);
            releaseTimeout.current = setTimeout(() => {
              setTurn(prev => (prev === 'speaking' ? 'prompt' : prev));
            }, AGENT_RELEASE_MS);
          }
        }}
      />
    );
  }, [agentAudioTrack]);

  const onDataReceived = useCallback(msg => {
    console.log('onDataReceived', msg);
  }, []);
  useDataChannel(e => console.log('useDataChannel', e));

  return (
    <>
      {chatTileContent}
      {oneToOneMeetingStatus && (
        <LiveKitTranscriptPanel
          sourceKey={transcriptSourceKey}
          sourceText={transcriptSourceText}
        />
      )}
      {oneToOneMeetingStatus &&
      <LiveKitTextInput
        status={turn}
        showMicPermissionWarning={showMicPermissionWarning}
        onMessageSent={(msg) => {
          // A typed answer also hands the turn to the tutor.
          setTurn('thinking');
          // Reflect typed message in transcript UI (non-stacking).
          setTranscriptSourceKey(`player:typed_${Date.now()}`);
          setTranscriptSourceText(msg || '');
        }}
      />}
    </>
  );
}
